import styled from "styled-components";
import {
  ArrowUturnLeftIcon,
  IconButton,
  TextInput,
  Tooltip,
  Typography,
  XMarkIcon,
  useInterstateToken
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/core-labels";
import type { FeeWithUIState } from "../types";
import {
  blockNonAmountKey,
  formatAmountForInput,
  formatCurrency,
  sanitizeAmountInput
} from "../utils/format";
import { FeeGridRow, srOnlyInputLabel } from "../styles/FeeManager.styles";

const RowContainer = styled(FeeGridRow)<{
  $disabled?: boolean;
  $center?: boolean;
}>`
  // Top-align editable cells so a name error below the input doesn't shift the others; removed rows are plain text with no error, so center them.
  align-items: ${props => (props.$center ? "center" : "flex-start")};
  padding: 8px;
  border-radius: 4px;
  background-color: transparent;
  min-height: 48px;
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  transition: opacity 0.2s;

  ${srOnlyInputLabel}
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
`;

// Left-aligned in every row so the value lines up with the AMOUNT header and the totals band.
const AmountCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const ActionCell = styled.div<{ $center?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  // Editable rows pin to TextInput's medium control height so the button centers on the input, not the taller row a name error produces; removed rows center on the row itself.
  align-self: ${props => (props.$center ? "center" : "flex-start")};
  height: ${props => (props.$center ? "auto" : "40px")};
`;

export interface FeeRowProps {
  amountText?: string;
  disabled: boolean;
  fee: FeeWithUIState;
  hasNameError: boolean;
  locale?: string;
  shouldAutoFocusName: boolean;
  onAmountChange: (rowKey: string, text: string) => void;
  onDelete: (rowKey: string) => void;
  onNameBlur: (rowKey: string) => void;
  onNameChange: (rowKey: string, name: string) => void;
  onRestore: (rowKey: string) => void;
}

export const FeeRow = ({
  amountText,
  disabled,
  fee,
  hasNameError,
  locale = "en-US",
  shouldAutoFocusName,
  onAmountChange,
  onDelete,
  onNameBlur,
  onNameChange,
  onRestore
}: FeeRowProps) => {
  const getToken = useInterstateToken();
  const errorColor =
    getToken("sem.color.on-surface.error.default") ?? "#c5281c";

  const isFrenchCanadian = locale === "fr-CA";
  const currencyProps = isFrenchCanadian
    ? { inputSuffix: "$" }
    : { inputPrefix: "$" };

  // The displayed amount is always the in-progress buffer when one exists.
  // Deriving it from fee.amount instead would let Interstate's TextInput
  // re-sync effect — which fires on hasError and disabled, not just value —
  // overwrite text like "1200." mid-edit.
  const amountValue = amountText ?? formatAmountForInput(fee.amount, locale);

  // Every row has the same buttons, so the name tells screen-reader users which row.
  const withFeeName = (labelKey: string) => {
    const label = String(Locale.getText(labelKey));
    return fee.name.trim() ? `${label} ${fee.name.trim()}` : label;
  };

  if (fee.removed) {
    return (
      <RowContainer
        $center
        $disabled={disabled}
        data-testid={`fee-row-${fee.rowKey}`}
      >
        <NameCell>
          <Typography variant={"body-md"}>{fee.name}</Typography>
        </NameCell>

        <AmountCell>
          <Typography variant={"body-md"}>
            {formatCurrency(fee.amount, locale)}
          </Typography>
        </AmountCell>

        <ActionCell $center data-fee-action={fee.rowKey}>
          <Tooltip
            position="top"
            size="small"
            toolTipContent={String(Locale.getText("RESTORE"))}
          >
            <IconButton
              aria-label={withFeeName("RESTORE")}
              buttonStyle="tertiary"
              disabled={disabled}
              size="sm"
              onClick={() => onRestore(fee.rowKey)}
            >
              <ArrowUturnLeftIcon
                aria-hidden="true"
                title={String(Locale.getText("RESTORE"))}
              />
            </IconButton>
          </Tooltip>
        </ActionCell>
      </RowContainer>
    );
  }

  return (
    <RowContainer $disabled={disabled} data-testid={`fee-row-${fee.rowKey}`}>
      <NameCell>
        <TextInput
          autoFocus={shouldAutoFocusName}
          data-testid={`fee-name-input-${fee.rowKey}`}
          disabled={disabled}
          errorMessage={String(Locale.getText("FEEMANAGER_FEENAME_ERROR"))}
          hasError={hasNameError}
          id={`fee-name-${fee.rowKey}`}
          label={String(Locale.getText("FEEMANAGER_NAME"))}
          name={`fee-name-${fee.rowKey}`}
          placeholder={String(Locale.getText("FEEMANAGER_FEENAME_PLACEHOLDER"))}
          value={fee.name}
          onBlur={() => onNameBlur(fee.rowKey)}
          onChange={e => onNameChange(fee.rowKey, e.target.value)}
        />
      </NameCell>

      <AmountCell>
        <TextInput
          data-testid={`fee-amount-input-${fee.rowKey}`}
          disabled={disabled}
          id={`fee-amount-${fee.rowKey}`}
          label={String(Locale.getText("FEEMANAGER_AMOUNT"))}
          name={`fee-amount-${fee.rowKey}`}
          placeholder={String(Locale.getText("FEEMANAGER_AMOUNT_PLACEHOLDER"))}
          value={amountValue}
          {...currencyProps}
          onChange={e =>
            onAmountChange(
              fee.rowKey,
              sanitizeAmountInput(e.target.value, locale)
            )
          }
          onKeyDown={e =>
            blockNonAmountKey(
              e as React.KeyboardEvent<HTMLInputElement>,
              amountValue,
              locale
            )
          }
        />
      </AmountCell>

      <ActionCell data-fee-action={fee.rowKey}>
        <Tooltip
          position="top"
          size="small"
          toolTipContent={String(Locale.getText("DELETE"))}
        >
          <IconButton
            aria-label={withFeeName("DELETE")}
            buttonStyle="tertiary"
            disabled={disabled}
            size="sm"
            onClick={() => onDelete(fee.rowKey)}
          >
            <XMarkIcon
              aria-hidden="true"
              style={{ color: errorColor }}
              title={String(Locale.getText("DELETE"))}
            />
          </IconButton>
        </Tooltip>
      </ActionCell>
    </RowContainer>
  );
};
